import React, { useState, FC, useEffect, useMemo, useCallback } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, RowData, SortingState, useReactTable} from '@tanstack/react-table';
import {  Row, Col, Pagination, Form, Spinner, Button, OverlayTrigger, Tooltip, Modal, Dropdown } from 'react-bootstrap';
import { formatNumber, getNameByISO3 } from "../../../../libs/misc";

import { SimulationFormRow } from '../core/_models';
import { useFTAImpactContext } from '../../../../contexts/fta-impact/FTAImpactContext';
import { useNavigate } from 'react-router-dom';
import { getSimulationData, getILODatasetTypes } from '../core/_requests';
import useAxiosPrivate from "../../../../libs/useAxiosPrivate";
import { IO_SETTING_GUIDE } from "../../../../libs/constant";
import clsx from 'clsx';
import InputOutputDropZone from './InputOutputDropZone';
import { Toast } from '../../../../../ui-kit';
import { useAuth } from '../../../../modules/auth';

declare module '@tanstack/react-table' { 
  interface ColumnMeta<TData extends RowData, TValue> { 
    headerClass: string; 
    cellClass:string;
    updateData?: (rowIndex: any, value: any) => void;
  }
}

function useSkipper() {
  const shouldSkipRef = React.useRef(true)
  const shouldSkip = shouldSkipRef.current

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = React.useCallback(() => {
    shouldSkipRef.current = false
  }, [])

  React.useEffect(() => {
    shouldSkipRef.current = true
  })

  return [shouldSkip, skip] as const
}

const columnHelper = createColumnHelper<SimulationFormRow>()

interface InputOutputFormTableProps {
  onSimulationInputChange: (input: any) => void;
  onIloTypeChange: (value: number) => void;
  onSubmitSimulation: () => void;
}

const EditableCell = (cellInfo) => {
  const [value, setValue] = useState(cellInfo.getValue())
  const rowId = cellInfo.row.original.oecd_code

  const onBlur = () => {
    cellInfo.column.columnDef.meta.updateData(rowId, value)
  }

  const onChange = (e) => {
    // Remove commas to treat as raw number
    const val = e.target.value.replace(/,/g, '')
    // Update state only if it's a valid numeric prefix or empty
    if (!isNaN(val as any) || val === '' || val === '-') {
      setValue(val)
    }
  }

  return (
    <Form.Control
      type="text"
      size="sm"
      value={value === '' || value === '-' ? value : formatNumber(value)}
      onChange={onChange}
      onBlur={onBlur}
    />
  )
}


const InputOutputFormTable: FC<InputOutputFormTableProps> =  ({
  onSimulationInputChange,
  onIloTypeChange,
  onSubmitSimulation
}) => {
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const {dataTable, year, reporter, partner } = useFTAImpactContext()
  const [simulationInput, setSimulationInput] = useState<SimulationFormRow[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper()
  const [useMirrorBase, setUseMirrorBase] = useState<boolean>(false)
  const [modalParam, setModalParam] = useState({
    show:false,
    header:'',
    body:''
  })
  const [iloDatasetTypes, setILODatasetTypes] = useState<any[]>([])
  const [selectedILODatasetType, setSelectedILODatasetType] = useState<number>(1)
  const { currentUser } = useAuth();
  const reporterISO3User = currentUser?.economy;
  const reporterNameUser = getNameByISO3(reporterISO3User);
  
  const handleDisplayToggle = (param:any=undefined) => {
    if(param && !modalParam.show===true){
      return setModalParam((prev)=>{
        return{
          ...prev,
          show: !modalParam.show,
          header: param.header,
          body:param.body
        }
      })
    }
    return setModalParam((prev)=>{
      return{
        ...prev,
        show: !modalParam.show
      }
    })
  }

  const baseColumns = [
    columnHelper.accessor('oecd_code', {
      header: 'ISIC Rev.4',
      cell: info => {
        let hsCodes;
        if(info.row.original.hs_code_concordance && info.row.original.hs_code_concordance.length>0 ){
          hsCodes = info.row.original.hs_code_concordance
        }
        return (
          <span className="badge text-white bg-dark w-50px d-inline-block" title={hsCodes}>
            {info.getValue()}
          </span>
        )
      },
      meta: {
        headerClass: 'text-center',
        cellClass: 'text-center'
      }
    }),
    columnHelper.accessor('desc', {
      header: 'Sector Description',
      cell: info => (
        <small>
          {info.getValue()}
        </small>
      ),
      meta: {
        headerClass: 'w-200px',
        cellClass: 'w-200px text-left'
      }
    }),
    columnHelper.accessor('export_wld', {
      header: `${reporterNameUser} Exports to World`,
      cell: info => formatNumber(info.getValue()),
      meta: {
        headerClass: clsx('max-w-100px', {'selected-col':!useMirrorBase}),
        cellClass: clsx('text-end',{'selected-col':!useMirrorBase})
      }
    }),
    columnHelper.accessor('export_mir_wld', {
      header: `Mirrored Data of ${reporterNameUser} Exports to World`,
      cell: info => formatNumber(info.getValue()),
      meta: {
        headerClass: clsx('max-w-100px', {'selected-col':useMirrorBase}),
        cellClass: clsx('text-end',{'selected-col':useMirrorBase})
      }
    }),
    columnHelper.accessor('import', {
      header: `Import of ${partner} from ${reporterNameUser}`,
      cell: info => formatNumber(info.getValue()),
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end'
      }
    }),
    columnHelper.accessor('domestic_export_change', {
      header: `Total Export Change`,
      cell: (cellInfo) => EditableCell(cellInfo),
      meta: {
        headerClass: 'max-w-100px',
        cellClass: 'text-end',
        updateData: (rowIndex, value) =>{
          skipAutoResetPageIndex()
          setSimulationInput((prev)=>{
            return (prev ?? []).map((item) =>
              item.oecd_code === rowIndex ? { 
                ...item, 
                domestic_export_change: +value,
                multiplier: calculateMultiplier(+value, item.export_wld, item.export_mir_wld)
              } 
              : item
            );
          })
        
        }
      }
    })
  ]
  const inputRowsMemo = useMemo(()=> simulationInput || [], [simulationInput])

  const formDataTable = useReactTable({
    data:inputRowsMemo,
    columns: partner==='WLD'?baseColumns.filter((_, index) => index !== 4):baseColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: {
      pagination,
      globalFilter,
      sorting
    },
    autoResetPageIndex,
    autoResetExpanded: false,
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1;
    const totalPages = formDataTable.getPageCount();
    const maxPagesToShow = 3;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range:any = [];

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }

    return range;
  }

  const handlePageChange = (page) => {
    const currentPage = pagination.pageIndex+1
    const totalPages = formDataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      formDataTable.setPageIndex(page-1)
    }
  };

  const handleDisplayPageChange = (e) =>{
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value
    })
  }

  const handleUseMirrorBaseChange = (e) =>{
    setUseMirrorBase(e.target.checked)
    setSimulationInput((prev)=>{
      return (prev ?? []).map((item) => {
        return {
          ...item, 
          multiplier: calculateMultiplier(item.domestic_export_change, item.export_wld, item.export_mir_wld)
        }
      });
    })
  }
  
  const handleILODatasetTypeChange = (eventKey: any) => {
    const value = +eventKey
    setSelectedILODatasetType(value)
    onIloTypeChange(value)
  }

  const calculateMultiplier = (value, exportValue, mirExportValue) =>{
    if(useMirrorBase){
      return (+value+mirExportValue)/+mirExportValue
    }
    return (+value+exportValue)/+exportValue
  }

  const generateLinkBody =  (content, link) =>{
    const updatedString = content.replace(/INSERT_LINK/g, link)
    return updatedString
  }

  const pages = paginationRange();
  const totalCount = formDataTable.getRowCount()
  const firstIndexPerPages = ((formDataTable.getState().pagination.pageIndex) * formDataTable.getState().pagination.pageSize) + 1
  const lastIndexPerPages = ((formDataTable.getState().pagination.pageIndex) * formDataTable.getState().pagination.pageSize) + formDataTable.getRowModel().rows.length
  const displayHeader = formDataTable.getHeaderGroups().map(headerGroup=>(
    <tr className='fw-normal py-4 text-center align-middle' key={headerGroup.id}>
    {
      headerGroup.headers.map(header=>{
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div 
              className={header.column.getCanSort()
                  ? 'cursor-pointer select-none'
                  : ''
              }
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                      ? 'Sort descending'
                      : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              <span className="ms-2"> 
                {{
                  asc: (<i className="fa-solid fa-sort-up"></i>),
                  desc: (<i className="fa-solid fa-sort-down"></i>),
                  false: (<i className="fa-solid fa-sort"></i>),
                }[header.column.getIsSorted() as string] ?? null}
              </span>
              </div>
          </th>
        )
      })
    }
    </tr>
  ))
  const displayBody = formDataTable.getRowModel().rows.map(row=>(
    <tr key={row.id} className='align-middle'>
      {
        row.getVisibleCells().map(cell=>{
          return (
            <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
              {flexRender(
                cell.column.columnDef.cell, 
                cell.getContext())
              }
            </td>
          )
        })
      }
    </tr>
  ))
  // Cleanup function to reset the state when the component is unmounted 
  useEffect(()=>{
    setSimulationInput([])
    setIsLoading(true)
    return () => {
      setSimulationInput([])
      setIsLoading(true)
    };
  },[])

  useEffect(() => {
    getILODatasetTypes(axiosPrivate)
      .then(res => {
        setILODatasetTypes(res.data.data)
        if(res.data.data.length > 0) {
          onIloTypeChange(selectedILODatasetType)
        }
      })
      .catch(err => {
        console.error('Error fetching ILO dataset types:', err)
      })
  }, [axiosPrivate])

  useEffect(() => {
    if (dataTable && dataTable.length > 0) {
      const listHsCodes = dataTable.map((row:any) => row.original.hs_code);
      getSimulationData(axiosPrivate, {
        rep:reporterISO3User,
        par:partner,
        year:year,
        hsCodes:listHsCodes
      })
      .then(res=>{
        const validHSCodes = new Set<string>();
        let totalExportChange = 0;

        const inputData = res.data.data.rows.map((row=>{
          let export_change = 0
          const selectedHsCode = row.hs?.split(", ").filter(hs=>listHsCodes.includes(hs))
          
          selectedHsCode?.forEach((hs) => validHSCodes.add(hs));
          if (selectedHsCode?.length > 0) {
            const selectedSimulationData = dataTable.filter((data: any) => selectedHsCode.includes(data.original.hs_code));
            if (selectedSimulationData.length > 0) {
              if(reporter===reporterISO3User){
                export_change = selectedSimulationData.reduce((acc: number, data: any) => {
                  if(data.original.tc+data.original.td === 0){
                    validHSCodes.delete(data.original.hs_code)
                    return acc
                  }
                  return acc + (data.original.tc+data.original.td)
                }, 0);
              }else{
                export_change = selectedSimulationData.reduce((acc: number, data: any) => {
                  const impactValue = data.original.affected_econ?.find(row=>row.econ===reporterISO3User)
                  if(impactValue){
                    return acc + (impactValue.tc+impactValue.td)
                  }
                  return acc
                }, 0);
              }
            }
            totalExportChange+= export_change;
          }
          return{
            oecd_code: row.code,
            hs_code_concordance: selectedHsCode?.join(", "),
            desc: row.desc,
            export_wld: row.exports_to_wld,
            export_mir_wld: row.exports_mir_wld,
            import: row.import,
            domestic_export_change: export_change,
            multiplier: calculateMultiplier(export_change, row.exports_to_wld, row.exports_mir_wld),
          }
        }))

        const invalidIndexes = dataTable.reduce((acc: number[], data: any, index: number) => {
          if (!validHSCodes.has(data.original.hs_code)) acc.push(index+2);
          return acc;
        }, []);
        Toast(
          'Success Preparing Simulation Data',
          'success',
          `There are total of ${validHSCodes.size} HS Code(s) valid for the simulation data with total change of ${formatNumber(totalExportChange)} USD$.`,
          false
        )
        if(invalidIndexes.length > 0){
          Toast(
            `There are total ${invalidIndexes.length} invalid data(s)`,
            'info',
            "The following HS Codes are not found / invalid code for the simulation data in rows: " + invalidIndexes,
            false
          )
        }
        setSimulationInput(inputData)
        onSimulationInputChange(inputData)
      })
      .catch(err=>{
        console.error(err)
        Toast(
          'Something Went Wrong',
          'danger',
          err
        )
      })
      setIsLoading(false)
    } else {
      setIsLoading(false)
    }
  }, [dataTable])

  useEffect(()=>{
    onSimulationInputChange(simulationInput);
  },[onSimulationInputChange, simulationInput])

  useEffect(()=>{
    
  },[useMirrorBase])

  if (!dataTable || !reporter || !partner) {
    return (
      <>
        <div className='row'>

          <div className="alert alert-warning d-flex justify-content-between align-items-center">
            <span>Please run the RTA Impact Simulation first.</span>
            <div>
              <Button onClick={() => navigate(`/third-party-impacts?rep=${reporterISO3User}`)} className="btn btn-primary me-2">
                RTA Simulation ({reporterNameUser})
              </Button>
              <Button onClick={() => navigate('/third-party-impacts')} className="btn btn-primary">
                RTA Simulation (Other countries)
              </Button>
            </div>
          </div>
        </div>
        <div className='row text-center my-5'>
          <h3>OR</h3>
        </div>
        <div className='row text-center'>
          <InputOutputDropZone/>
        </div>
      </>
    );
  } else{
    return (
      <>
        {!isLoading && (simulationInput?.length??0)>0 ? (
          <>
            <Row>
              <Col className='d-flex flex-column align-items-end mb-4'>
                <div className='d-flex align-items-center mb-2'>
                  <Form.Check
                    reverse
                    type="switch"
                    id="custom-switch"
                    label={`Use ${reporterNameUser}'s mirror trade data`}
                    onChange={(e)=>handleUseMirrorBaseChange(e)}
                  />
                  <i 
                    className="ms-2 fa-solid fa-circle-question text-primary cursor-pointer me-3"
                    onClick={()=>handleDisplayToggle(IO_SETTING_GUIDE.MIRROR_DATA_SWITCH)}
                  ></i>
                </div>
                <div className='d-flex align-items-center'>
                  <span className='me-2'>Labour dataset category:</span>
                  <Dropdown onSelect={handleILODatasetTypeChange}>
                    <Dropdown.Toggle 
                      variant="outline" 
                      size="sm" 
                      className='form-select-sm w-300px text-start d-flex justify-content-between align-items-center py-2'
                      title={iloDatasetTypes?.find(t => t.id === selectedILODatasetType)?.label || 'Select Category'}
                    >
                      <span className='text-truncate'>
                        {iloDatasetTypes?.find(t => t.id === selectedILODatasetType)?.label || 'Select Category'}
                      </span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="w-300px shadow-sm">
                      {iloDatasetTypes?.map((item: any) => (
                        <Dropdown.Item key={item.id} eventKey={item.id} className="text-wrap py-2 fs-7">
                          {item.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                   <i 
                    className="ms-2 fa-solid fa-circle-question text-primary cursor-pointer me-3"
                    onClick={()=>handleDisplayToggle(IO_SETTING_GUIDE.LABOUR_DATASET_DROPDOWN)}
                    ></i>

                </div>
              </Col>
            </Row>
                <Modal 
                  show={modalParam.show} onHide={handleDisplayToggle} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>
                      <i className="fa fa-exclamation-circle me-3"></i>
                      {modalParam.header}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                      <p dangerouslySetInnerHTML={{__html: generateLinkBody(modalParam.body, `${process.env.REACT_APP_TINA_BASE_URL}/app/dashboard/${reporter}-${partner}/trade-discrepancies`)}}></p>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="outline" onClick={handleDisplayToggle}>
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>

            <Row className='my-3 mb-6'>

              <Col md={6} className='d-flex justify-content-start align-items-center'>
                <i className="fa-solid fa-circle text-success fs-9 me-2"></i>Showing {firstIndexPerPages} to {lastIndexPerPages} of {totalCount} simulations results
                <Button 
                  variant="warning" 
                  size="sm" 
                  className="ms-3 text-black fw-bold" 
                  disabled={!simulationInput || !(simulationInput?.length>0)}
                  onClick={onSubmitSimulation}>
                  Submit & Simulate
                </Button>
              </Col>
              <Col md={6} className='d-flex justify-content-end align-items-center'>
                {/* <Form.Group as={Row} controlId="">
                  <Form.Label className="text-end text-primary me-0" column>
                    <i className="fa-solid fa-magnifying-glass fa-rotate-90 text-primary fs-9 me-2"></i>Search :
                  </Form.Label>
                  <Col md={6} className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      className="form-control-sm"
                      onChange={e => formDataTable.setGlobalFilter(String(e.target.value))} />
                    </Col>
                </Form.Group> */}
              </Col>
            </Row>
            <div className="table-responsive">
              <table className="gy-3 table table-striped table-bordered table-hover" id="impact_result_table">
                <thead className='table-dark'>
                  {displayHeader}
                </thead>    
                <tbody className="gy-1 overflow-auto">
                  {displayBody}
                </tbody>
              </table>
            </div>
            {/* <Row className='my-3'>
                <Col md={6} className='d-flex justify-content-start align-items-center'>
                  Display
                  <Form.Select
                    size="sm"
                    className="w-80px mx-2"
                    onChange={handleDisplayPageChange}
                    value={pagination.pageSize}>
                    <option value="10">10</option>
                    <option value="50">50</option>
                  </Form.Select>
                  simulation result
                </Col>
                <Col md={6} className='d-flex justify-content-end'>
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => formDataTable.previousPage()}
                      disabled={!formDataTable.getCanPreviousPage()} />
                    {pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <Pagination.Ellipsis key={index} />
                        );
                      }
                      return (
                        <Pagination.Item
                          key={index}
                          active={page === pagination.pageIndex + 1}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      onClick={() => formDataTable.nextPage()}
                      disabled={!formDataTable.getCanNextPage()} />
                  </Pagination>
                </Col>
            </Row> */}
          </>
        ):(
          <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
            <Spinner animation="border" role="status" variant="primary" className="me-3" />
            <div className="loading-text">
              Loading
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </div>
          </div>
        )}
      </>
    )
  }
}

export default InputOutputFormTable